// ANI · presentation · referans galeri · 12 görsel · 4 sayfa pagination

import { useRef, useState, useCallback, useEffect } from "react";
import { SectionShell } from "@/features/landing/components/SectionShell";
import { Lightbox, type LightboxImage } from "@/components/ui/Lightbox";
import { COPY } from "@/features/landing/content/landing-copy";
import { useAniMotion } from "./Ani.motion";
import styles from "./Ani.module.css";

const PER_PAGE = COPY.ani.pagination.perPage;

const LIGHTBOX_IMAGES: readonly LightboxImage[] = COPY.ani.gallery.map((shot) => ({
  src: `/${shot.src}`,
  alt: shot.alt,
  caption: shot.client,
  subcaption: `${shot.scope} · ${shot.year}`,
}));

export function Ani() {
  const scopeRef = useRef<HTMLElement | null>(null);
  useAniMotion(scopeRef);

  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState<number | null>(null);
  const totalPages = Math.ceil(COPY.ani.gallery.length / PER_PAGE);
  const start = page * PER_PAGE;
  const visible = COPY.ani.gallery.slice(start, start + PER_PAGE);

  const goPrev = useCallback(() => setPage((p) => (p - 1 + totalPages) % totalPages), [totalPages]);
  const goNext = useCallback(() => setPage((p) => (p + 1) % totalPages), [totalPages]);

  // Klavye ile gezinme (sol/sağ ok) — lightbox açıkken devre dışı
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (document.body.hasAttribute("data-lightbox-open")) return;
      if (e.target instanceof HTMLElement && e.target.closest("[data-ani-pager]")) return;
      if (e.key === "ArrowLeft")  setPage((p) => (p - 1 + totalPages) % totalPages);
      if (e.key === "ArrowRight") setPage((p) => (p + 1) % totalPages);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [totalPages]);

  const counter = COPY.ani.pagination.counterTemplate
    .replace("{current}", String(page + 1))
    .replace("{total}", String(totalPages));

  return (
    <SectionShell id="ani" ariaLabelledBy="ani-title" tone="paper-warm" container="wide">
      <div className={styles.ani} ref={scopeRef as unknown as React.RefObject<HTMLDivElement>}>
        <header className={styles.ani__head}>
          <div>
            <p className={styles.ani__eyebrow}>{COPY.ani.chapter} · {COPY.ani.eyebrow}</p>
            <h2 className={styles.ani__title} id="ani-title">{COPY.ani.title}</h2>
          </div>
          <p className={styles.ani__caption}>{COPY.ani.caption}</p>
        </header>

        <ol className={styles.ani__grid} role="list" aria-label="Referans projeler" data-ani-pager>
          {visible.map((shot, i) => {
            const kindClass = styles[`shot--${shot.kind}`] || "";
            return (
              <li
                key={`${page}-${i}`}
                className={`${styles.shot} ${kindClass}`.trim()}
              >
                <img
                  src={`/${shot.src}`}
                  alt={shot.alt}
                  width={1600}
                  height={shot.kind === "hero" ? 1000 : 1067}
                  loading="eager"
                  decoding="async"
                />
                <button
                  type="button"
                  className={styles.shot__zoom}
                  onClick={() => setZoom(start + i)}
                  aria-label={`${shot.client} görselini büyüt`}
                />
                <span className={styles.shot__year}>{shot.year}</span>
                <figcaption className={styles.shot__meta}>
                  <span className={styles.shot__client}>{shot.client}</span>
                  <span className={styles.shot__scope}>{shot.scope}</span>
                </figcaption>
              </li>
            );
          })}
        </ol>

        {/* ── Pagination ────────────────────────────────────── */}
        <nav className={styles.ani__pager} aria-label="Galeri sayfaları" data-ani-pager>
          <button
            type="button"
            className={`${styles["ani__pager-btn"]} ${styles["ani__pager-btn--prev"]}`}
            onClick={goPrev}
            aria-label={COPY.ani.pagination.prevLabel}
          >
            <span className={styles["ani__pager-arrow"]} aria-hidden="true">←</span>
            <span>{COPY.ani.pagination.prevLabel}</span>
          </button>

          <ol className={styles["ani__pager-dots"]}>
            {Array.from({ length: totalPages }).map((_, i) => (
              <li key={i}>
                <button
                  type="button"
                  className={`${styles["ani__pager-dot"]} ${i === page ? styles["ani__pager-dot--active"] : ""}`}
                  onClick={() => setPage(i)}
                  aria-label={`${COPY.ani.pagination.counterTemplate.replace("{current}", String(i + 1)).replace("{total}", String(totalPages))}`}
                  aria-current={i === page ? "page" : undefined}
                />
              </li>
            ))}
          </ol>

          <span className={styles["ani__pager-counter"]}>{counter}</span>

          <button
            type="button"
            className={`${styles["ani__pager-btn"]} ${styles["ani__pager-btn--next"]}`}
            onClick={goNext}
            aria-label={COPY.ani.pagination.nextLabel}
          >
            <span>{COPY.ani.pagination.nextLabel}</span>
            <span className={styles["ani__pager-arrow"]} aria-hidden="true">→</span>
          </button>
        </nav>

        <dl className={styles.ani__marginalia}>
          {COPY.ani.marginalia.map((m, i) => {
            const pairs: Array<[string, string]> = [
              ["Aktivasyon", "240+"],
              ["Ziyaretçi", "4.8M+"],
              ["Fire Oranı", "0%"],
              ["Deneyim", "12 yıl"],
            ];
            return (
              <div key={m} className={styles["ani__marginalia-item"]}>
                <dt>{pairs[i]?.[0] ?? m}</dt>
                <dd>{pairs[i]?.[1] ?? m}</dd>
              </div>
            );
          })}
        </dl>
      </div>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        index={zoom}
        onClose={() => setZoom(null)}
        onNavigate={setZoom}
        label="Referans galerisi"
      />
    </SectionShell>
  );
}